import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Check } from 'lucide-react';

const programs = [
  {
    num: '01',
    title: 'Nursery',
    age: 'Ages 2–5',
    description:
      'A nurturing environment where children develop foundational skills through play-based learning.',
    features: ['Play-based Learning', 'Creative Arts', 'Social Development', 'Basic Literacy'],
    accent: 'from-orange-400/20 to-amber-300/10',
  },
  {
    num: '02',
    title: 'Primary 1–6',
    age: 'Ages 6–11',
    description:
      'Comprehensive primary education focusing on core subjects and character development.',
    features: ['Core Curriculum', 'STEM Programs', 'Leadership Training', 'Character Education'],
    accent: 'from-emerald-400/20 to-teal-300/10',
  },
  {
    num: '03',
    title: 'Secondary',
    age: 'Ages 12–17',
    description:
      'Advanced academic program preparing students for higher education and future careers.',
    features: ['NECO Preparation', 'Science & Tech', 'Arts & Humanities', 'Career Guidance'],
    accent: 'from-blue-400/20 to-indigo-300/10',
  },
  {
    num: '04',
    title: 'Special',
    age: 'All Ages',
    description:
      'Enrichment programs including arts and sports to develop well-rounded individuals.',
    features: ['Arts & Music', 'Sports Excellence', 'Technology Labs', 'Community Service'],
    accent: 'from-violet-400/20 to-purple-300/10',
  },
];

const values = [
  {
    title: 'Excellence',
    text: 'Unparalleled standard of intellectual growth and discovery that pushes every student further.',
  },
  {
    title: 'Character',
    text: 'Instilling confidence, discipline, and high moral values in every young mind we nurture.',
  },
  {
    title: 'Vision',
    text: 'Molding children to interact favourably with their world and tolerate others gracefully.',
  },
];

/* ── Individual card — uses useInView for viewport-triggered entrance ── */
interface CardProps {
  prog: typeof programs[number];
  index: number;
}

const ProgramCard = ({ prog, index }: CardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 70 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.7,
        delay: index * 0.12,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative"
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="relative h-full bg-white rounded-2xl p-6 sm:p-7 border border-zinc-100 shadow-[0_4px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)] transition-shadow overflow-hidden flex flex-col"
      >
        {/* Gradient accent blob */}
        <div
          className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${prog.accent} blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
        />

        {/* Number badge */}
        <div className="w-12 h-12 rounded-2xl bg-primary/8 flex items-center justify-center mb-5 group-hover:bg-primary group-hover:shadow-[0_0_24px_rgba(249,115,22,0.4)] transition-all duration-500">
          <span className="font-black text-primary text-lg group-hover:text-white transition-colors duration-500">
            {prog.num}
          </span>
        </div>

        {/* Age tag */}
        <span className="inline-block text-[10px] font-black uppercase tracking-widest text-primary/70 bg-primary/8 px-3 py-1 rounded-full mb-4 w-fit">
          {prog.age}
        </span>

        <h3 className="font-display font-black text-2xl text-zinc-900 tracking-tight mb-3 group-hover:text-primary transition-colors duration-300">
          {prog.title}
        </h3>

        <p className="text-zinc-500 font-medium leading-relaxed mb-8 flex-grow text-[15px]">
          {prog.description}
        </p>

        <ul className="space-y-2.5">
          {prog.features.map((f) => (
            <li key={f} className="flex items-center gap-3 text-sm font-semibold text-zinc-700">
              <span className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Check size={10} className="text-primary" strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.div>
  );
};

/* ── Section heading — fades in from below ── */
const SectionHeading = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="text-center mb-10 sm:mb-12"
    >
      <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-4 sm:mb-5">
        What We Offer
      </p>
      <h2 className="font-display font-black text-[clamp(2.2rem,7vw,4.5rem)] text-zinc-900 tracking-tighter leading-[0.9] mb-4 sm:mb-6">
        Academic <span className="text-gradient">Programs</span>
      </h2>
      <p className="text-zinc-500 text-base sm:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
        Nurturing every aspect of development — from first steps in Nursery to the final
        chapter of Secondary.
      </p>
    </motion.div>
  );
};

/* ── Mission / values block ── */
const MissionBlock = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative mt-16 sm:mt-28 rounded-[2rem] sm:rounded-[3rem] overflow-hidden bg-[#07091a] px-6 sm:px-10 md:px-16 py-12 sm:py-16 md:py-20"
    >
      {/* Parallax background texture */}
      <motion.div
        style={{ y: bgY }}
        aria-hidden
        className="absolute inset-0 scale-110 will-change-transform"
      >
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '42px 42px',
          }}
        />
      </motion.div>

      {/* Ambient blobs */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-primary/8 rounded-full blur-[80px]" />
      <div className="absolute bottom-0 left-0 w-56 h-56 bg-blue-600/6 rounded-full blur-[80px]" />

      <div className="relative z-10 text-center mb-14">
        <p className="text-primary text-[10px] font-black tracking-[0.35em] uppercase mb-4">
          Our Purpose
        </p>
        <h3 className="font-display font-black text-[clamp(2rem,5vw,3.5rem)] text-white tracking-tighter leading-tight mb-6">
          Mission &amp; Values
        </h3>
        <p className="text-slate-300 text-lg font-medium max-w-3xl mx-auto leading-relaxed">
          To be a leading educational provider by researching and acquiring new skills,
          adapted with efficiency and delivered with passion.
        </p>
      </div>

      <div className="relative z-10 grid md:grid-cols-3 gap-10 text-center mb-14">
        {values.map((v, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.04 }}
            className="p-6 rounded-2xl bg-white/4 border border-white/8 hover:bg-white/7 transition-colors"
          >
            <h4 className="font-black text-primary text-xl mb-3 tracking-tight">{v.title}</h4>
            <p className="text-slate-300 font-medium leading-relaxed text-[15px]">{v.text}</p>
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 text-center">
        <span className="inline-block px-7 py-2.5 rounded-full bg-primary/12 border border-primary/25 text-primary/80 font-bold italic tracking-wide text-sm">
          "Our God, Our Vision, Our Strength"
        </span>
      </div>
    </motion.div>
  );
};

const Programs = () => (
  <section id="programs" className="py-16 sm:py-20 lg:py-24 bg-[#f8f9fc] relative overflow-hidden">
    {/* Subtle top divider */}
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />

    <div className="container mx-auto px-6">
      <SectionHeading />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7">
        {programs.map((prog, i) => (
          <ProgramCard key={prog.num} prog={prog} index={i} />
        ))}
      </div>

      <MissionBlock />
    </div>
  </section>
);

export default Programs;
